import React, { useState, useCallback, useMemo } from 'react';
import {
  Briefcase,
  Users,
  Code,
  GitBranch,
  Settings,
  Plus,
  Trash2,
  Edit2,
  X,
  Save,
  Check,
} from 'lucide-react';

// --- Utility Functions & Mock Data ---

// Mocking a professional logging/notification system (no 'alert()')
const useNotification = () => {
  const [message, setMessage] = useState('');
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState('info'); // info, success, danger

  const notify = (msg, msgType = 'info') => {
    setMessage(msg);
    setType(msgType);
    setVisible(true);
    setTimeout(() => setVisible(false), 3000);
  };
  
  return { notify, message, visible, type };
};

const MOCK_DEPARTMENTS = [
  { id: 'D01', name: 'Executive Leadership', headcount: 5 },
  { id: 'D02', name: 'Product Engineering', headcount: 45 },
  { id: 'D03', name: 'Finance & Accounting', headcount: 12 },
  { id: 'D04', name: 'Sales & Client Relations', headcount: 38 },
];

// Updated to use Indian Rupee (₹) and more relevant salary bands
const MOCK_ROLES = [
  { id: 'R101', deptId: 'D02', name: 'Senior Software Engineer', level: 'L4', salaryBand: '₹12,00,000 - ₹18,00,000', description: 'Designs, develops, and deploys high-quality software solutions.' },
  { id: 'R102', deptId: 'D02', name: 'Product Manager', level: 'L5', salaryBand: '₹18,00,000 - ₹27,00,000', description: 'Owns the product vision, strategy, and roadmap.' },
  { id: 'R103', deptId: 'D04', name: 'Sales Development Rep (SDR)', level: 'L2', salaryBand: '₹6,00,000 - ₹9,00,000', description: 'Generates qualified leads and creates new sales opportunities.' },
  { id: 'R104', deptId: 'D01', name: 'Chief Technology Officer (CTO)', level: 'Exec', salaryBand: 'Negotiated (CTC)', description: 'Oversees all technology and R&D strategies across the company.' },
];

const MOCK_HIERARCHY = {
    id: 'CEO',
    title: 'Chief Executive Officer',
    children: [
        { id: 'CTO', title: 'Chief Technology Officer', children: [
            { id: 'VP_ENG', title: 'VP of Engineering', children: [
              { id: 'ENG_MGR', title: 'Engineering Manager', children: [] }
            ] },
        ] },
        { id: 'CFO', title: 'Chief Financial Officer', children: [
            { id: 'CONTROLLER', title: 'Controller', children: [] },
        ] },
        { id: 'COO', title: 'Chief Operating Officer', children: [] },
    ]
};

// --- Reusable UI Components ---

// Button Primary: Darker, more solid look
const ButtonPrimary = ({ onClick, children, className = '', disabled = false, icon: Icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    // Increased size slightly (py-2.5 instead of py-2) but kept text-xs for density
    className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all duration-200 shadow-md flex items-center justify-center space-x-1
                focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 
                bg-teal-600 text-white hover:bg-teal-700 active:bg-teal-800 disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none ${className}`}
  >
    {Icon && <Icon className="w-3.5 h-3.5" />}
    <span style={{ fontFamily: 'Roboto, sans-serif' }}>{children}</span>
  </button>
);

// Button Secondary: Bordered, clean look
const ButtonSecondary = ({ onClick, children, className = '', icon: Icon }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors duration-200 border border-gray-300
                text-gray-700 hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 flex items-center justify-center space-x-1 ${className}`}
  >
    {Icon && <Icon className="w-3.5 h-3.5" />}
    <span style={{ fontFamily: 'Roboto, sans-serif' }}>{children}</span>
  </button>
);

const Badge = ({ children, type = 'info', className = '' }) => {
  let color = 'bg-gray-100 text-gray-600';
  if (type === 'teal') color = 'bg-teal-100 text-teal-700 font-medium';
  if (type === 'indigo') color = 'bg-indigo-100 text-indigo-700 font-medium';
  if (type === 'red') color = 'bg-red-100 text-red-700 font-medium';

  return (
    // Slightly larger badge text/padding
    <span className={`px-2 py-0.5 inline-flex text-[11px] leading-4 font-medium rounded-full ${color} ${className}`}
          style={{ fontFamily: 'Roboto, sans-serif' }}>
      {children}
    </span>
  );
};

// Panel for content containment - using Poppins for the title
const Panel = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white p-5 rounded-2xl shadow-lg border border-gray-100 transition-shadow hover:shadow-xl ${className}`}>
    {/* Increased panel title size to text-base */}
    <h2 className="text-base font-semibold text-gray-800 flex items-center mb-4 pb-3 border-b border-gray-100"
        style={{ fontFamily: 'Poppins, sans-serif' }}>
      <Icon className="w-4 h-4 mr-2 text-teal-600" />
      {title}
    </h2>
    {/* Increased body text size to text-sm */}
    <div className="text-sm text-gray-700 space-y-4" style={{ fontFamily: 'Roboto, sans-serif' }}>
      {children}
    </div>
  </div>
);

// Input/Select Styling
const FormField = ({ label, children, className = '' }) => (
  <div className={`flex flex-col space-y-1 ${className}`}>
    {/* Increased label size to text-xs */}
    <label className="text-xs font-medium text-gray-600" style={{ fontFamily: 'Poppins, sans-serif' }}>
      {label}
    </label>
    {children}
  </div>
);

const Input = ({ className = '', ...props }) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-lg text-xs transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white ${className}`}
    style={{ fontFamily: 'Roboto, sans-serif' }}
    {...props}
  />
);

const Select = ({ className = '', ...props }) => (
  <select
    className={`px-3 py-2 border border-gray-300 rounded-lg text-xs appearance-none bg-white transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-8 ${className}`}
    style={{ fontFamily: 'Roboto, sans-serif' }}
    {...props}
  >
    {props.children}
  </select>
);

const Textarea = ({ className = '', ...props }) => (
  <textarea
    rows="3"
    className={`px-3 py-2 border border-gray-300 rounded-lg text-xs transition-colors focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white ${className}`}
    style={{ fontFamily: 'Roboto, sans-serif' }}
    {...props}
  />
);


// --- Zoho/Keka Style Enterprise Modal ---

const EnterpriseModal = ({ isOpen, onClose, title, children, actions }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50 flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all scale-100 opacity-100"
        onClick={e => e.stopPropagation()} // Prevent closing when clicking inside
        style={{ fontFamily: 'Roboto, sans-serif' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {children}
        </div>

        {/* Footer */}
        {actions && (
          <div className="p-4 border-t border-gray-100 flex justify-end space-x-3 bg-gray-50 rounded-b-xl">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};


// --- Hierarchy Recursion Component ---

const HierarchyNode = ({ node, level = 0 }) => (
  <div className='relative ml-4'>
    {/* Connector Line (Vertical) */}
    {node.children && node.children.length > 0 && (
      <div className="absolute top-4 left-[-0.75rem] w-px h-[calc(100%-8px)] border-l border-gray-300"></div>
    )}

    {/* Using text-sm for better visibility */}
    <div className='flex items-start text-sm rounded-lg mt-3'> 
      
      {/* Horizontal Connector (if not root) */}
      {level > 0 && (
        <div className='absolute top-4 left-[-0.75rem] w-3 h-px border-t border-gray-300'></div>
      )}

      {/* Node Card */}
      <div className={`flex flex-col p-3 rounded-xl shadow-sm border transition-all duration-200 
                      ${level === 0 ? 'bg-teal-50 border-teal-200 shadow-lg' : 'bg-white border-gray-200 hover:shadow-md'}`}>
        <div className='flex items-center space-x-2'>
          <GitBranch className={`w-3.5 h-3.5 mr-1 flex-shrink-0 ${level === 0 ? 'text-teal-600' : 'text-indigo-500'}`} />
          {/* Using text-base for node title with Poppins */}
          <span className={`text-base ${level === 0 ? 'font-extrabold' : 'font-semibold'}`} style={{ fontFamily: 'Poppins, sans-serif' }}>
            {node.title}
          </span>
        </div>
        {/* Keeping ID text small but visible */}
        <span className='text-xs text-gray-500 italic mt-0.5 ml-5'>ID: {node.id}</span>
      </div>
    </div>
    
    {/* Render Children */}
    {node.children && node.children.length > 0 && (
      <div className="ml-4">
        {node.children.map(child => (
          <HierarchyNode key={child.id} node={child} level={level + 1} />
        ))}
      </div>
    )}
  </div>
);


// --- Feature Components ---

const DepartmentManagement = ({ departments, setDepartments, notify }) => {
  const [newDeptName, setNewDeptName] = useState('');

  const handleAddDepartment = () => {
    if (newDeptName.trim() === '') {
      return notify('Department name cannot be empty.', 'danger');
    }

    const newIdNum = departments.length + 1;
    const newDept = {
      id: `D${String(newIdNum).padStart(2, '0')}`,
      name: newDeptName.trim(),
      headcount: 0,
    };

    setDepartments(prev => [...prev, newDept]);
    setNewDeptName('');
    notify(`Department '${newDept.name}' added.`, 'success');
  };

  const handleRemoveDepartment = (id, name) => {
    if (departments.find(d => d.id === id)?.headcount > 0) {
        return notify(`Cannot remove '${name}'. Headcount is greater than 0.`, 'danger');
    }
    setDepartments(prev => prev.filter(d => d.id !== id));
    notify(`Department '${name}' removed.`, 'info');
  };

  return (
    <Panel title="Department & Team Structure" icon={Briefcase}>
      {/* Increased description text size to text-sm */}
      <p className='text-sm text-gray-500'>Define and manage top-level departments across the organization.</p>
      
      {/* Department List */}
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-1 shadow-inner">
        <div className='flex justify-between items-center text-xs font-semibold text-gray-500 pb-1 border-b border-gray-200' style={{ fontFamily: 'Poppins, sans-serif' }}>
            <span>Department Name</span>
            <span>Headcount</span>
        </div>
        {departments.length === 0 && <p className='text-gray-400 italic text-center text-sm py-4'>No departments defined.</p>}
        {departments.map((dept) => (
          <div key={dept.id} className="flex justify-between items-center p-2 rounded-lg bg-white hover:bg-teal-50 transition-colors border border-gray-100">
            <div className='flex flex-col'>
                {/* Increased department name text size to text-sm */}
                <span className="font-medium text-sm text-gray-800">{dept.name}</span> 
                {/* Keeping ID text small */}
                <span className='text-xs text-gray-500'>ID: {dept.id}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Badge type="teal" className='min-w-[60px] justify-center'>HC: {dept.headcount}</Badge>
              <button
                onClick={() => handleRemoveDepartment(dept.id, dept.name)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50"
                aria-label={`Remove department ${dept.name}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add New Department Form */}
      <div className="pt-4 border-t border-gray-100 space-y-2">
        {/* Increased heading text size to text-sm */}
        <h4 className="text-sm font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>Create New Department</h4>
        <div className='flex space-x-2'>
            <Input
                type="text"
                placeholder="E.g., Infrastructure Operations"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                className="flex-grow"
            />
            <ButtonPrimary onClick={handleAddDepartment} icon={Plus} disabled={!newDeptName.trim()}>
                Add Department
            </ButtonPrimary>
        </div>
      </div>
    </Panel>
  );
};

// Component for Role Creation/Editing within the Modal
const RoleForm = ({ role, setRole, departments }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRole(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField label="Role Name" className='col-span-2'>
        <Input
          type="text"
          name="name"
          placeholder="e.g., Lead UX Designer"
          value={role.name}
          onChange={handleInputChange}
          required
        />
      </FormField>

      <FormField label="Department">
        <Select
          name="deptId"
          value={role.deptId}
          onChange={handleInputChange}
          required
        >
          <option value="">-- Select Department --</option>
          {departments.map(dept => (
            <option key={dept.id} value={dept.id}>{dept.name}</option>
          ))}
        </Select>
      </FormField>

      <FormField label="Level/Grade">
        <Input
          type="text"
          name="level"
          placeholder="e.g., L6 or Senior"
          value={role.level}
          onChange={handleInputChange}
        />
      </FormField>

      <FormField label="Salary Band (Annual CTC in ₹)" className='col-span-2'>
        <Input
          type="text"
          name="salaryBand"
          // Updated placeholder to use Indian Rupees (₹)
          placeholder="e.g., ₹15,00,000 - ₹20,00,000" 
          value={role.salaryBand}
          onChange={handleInputChange}
        />
      </FormField>

      <FormField label="Role Description" className='col-span-2'>
        <Textarea
          name="description"
          placeholder="Brief description of responsibilities and required skills."
          value={role.description}
          onChange={handleInputChange}
        />
      </FormField>
    </div>
  );
}

const RoleManagement = ({ roles, departments, setRoles, notify }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null); // Role being edited/created
  
  const initialNewRoleState = useMemo(() => ({ 
    name: '', 
    deptId: departments[0]?.id || '', 
    level: '', 
    salaryBand: '',
    description: '',
  }), [departments]);
  
  const getDeptName = (deptId) => departments.find(d => d.id === deptId)?.name || 'Unknown';

  const handleOpenModal = (role = null) => {
    setCurrentRole(role || initialNewRoleState);
    setIsModalOpen(true);
  };

  const handleSaveRole = () => {
    if (!currentRole.name || !currentRole.deptId) {
        return notify('Role Name and Department are required.', 'danger');
    }

    if (currentRole.id) {
        // Edit existing role
        setRoles(prev => prev.map(r => r.id === currentRole.id ? currentRole : r));
        notify(`Role '${currentRole.name}' updated successfully.`, 'success');
    } else {
        // Create new role
        const roleId = `R${String(roles.length + 101).padStart(3, '0')}`;
        const finalRole = { ...currentRole, id: roleId };
        setRoles(prev => [...prev, finalRole]);
        notify(`Role '${finalRole.name}' created successfully.`, 'success');
    }

    setIsModalOpen(false);
    setCurrentRole(null);
  };

  const handleRemoveRole = (id, name) => {
    setRoles(prev => prev.filter(r => r.id !== id));
    notify(`Role '${name}' removed.`, 'info');
  };

  return (
    <>
      <Panel title="Role Definitions & Compensation Bands (₹)" icon={Code} className="h-full">
        {/* Increased description text size to text-sm */}
        <p className='text-sm text-gray-500'>Define specific job roles, levels, and associated compensation bands.</p>
        
        {/* Updated Button: Removed w-full and wrapped in a div for alignment */}
        <div className='flex justify-start mt-2 mb-4'>
            <ButtonPrimary onClick={() => handleOpenModal()} icon={Plus} className='w-auto'>
                Create New Role Definition
            </ButtonPrimary>
        </div>

        {/* Role List */}
        <div className="max-h-[50vh] overflow-y-auto border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2 shadow-inner">
          {roles.length === 0 && <p className='text-gray-400 italic text-center text-sm py-4'>No roles defined.</p>}
          {roles.map((role) => (
            <div key={role.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 rounded-lg bg-white hover:shadow-sm border border-gray-100 transition-shadow">
              <div className='flex flex-col flex-grow min-w-0 pr-4'>
                {/* Increased role name text size to text-sm */}
                  <span className="font-semibold text-sm text-gray-900 truncate" style={{ fontFamily: 'Poppins, sans-serif' }}>{role.name}</span>
                  <div className='flex flex-wrap items-center space-x-3 mt-1'>
                      <Badge type="indigo">{getDeptName(role.deptId)}</Badge>
                      {/* Using text-xs for the smaller detail text */}
                      <span className='text-xs text-gray-600 font-medium'>Level: {role.level || 'N/A'}</span>
                      <span className='text-xs text-gray-600 font-medium'>Band: {role.salaryBand || 'N/A'}</span>
                  </div>
              </div>
              <div className="flex items-center space-x-2 mt-2 sm:mt-0 flex-shrink-0">
                  <button
                      onClick={() => handleOpenModal(role)}
                      className="text-gray-500 hover:text-teal-600 transition-colors p-1 rounded-full hover:bg-teal-50"
                      aria-label={`Edit role ${role.name}`}
                  >
                      <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                      onClick={() => handleRemoveRole(role.id, role.name)}
                      className="text-gray-500 hover:text-red-600 transition-colors p-1 rounded-full hover:bg-red-50"
                      aria-label={`Remove role ${role.name}`}
                  >
                      <Trash2 className="w-4 h-4" />
                  </button>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Role Management Modal (EnterpriseModal) */}
      <EnterpriseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentRole?.id ? `Edit Role: ${currentRole.name}` : 'Create New Role Definition'}
        actions={
          <>
            <ButtonSecondary onClick={() => setIsModalOpen(false)} icon={X}>
              Cancel
            </ButtonSecondary>
            <ButtonPrimary onClick={handleSaveRole} icon={Save} disabled={!currentRole?.name || !currentRole?.deptId}>
              {currentRole?.id ? 'Save Changes' : 'Create Role'}
            </ButtonPrimary>
          </>
        }
      >
        {currentRole && (
          <RoleForm
            role={currentRole}
            setRole={setCurrentRole}
            departments={departments}
          />
        )}
      </EnterpriseModal>
    </>
  );
};

const HierarchyView = ({ hierarchy }) => (
    <Panel title="Organizational Hierarchy" icon={GitBranch} className="h-full">
        {/* Increased description text size to text-sm */}
        <p className='text-sm text-gray-500 mb-4'>Visualize the reporting structure from the top executive down.</p>
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 min-h-40 overflow-y-auto shadow-inner">
            <HierarchyNode node={hierarchy} />
        </div>
        {/* Increased helper text size to text-xs */}
        <div className='mt-4 p-3 border border-indigo-100 bg-indigo-50 rounded-lg text-xs text-indigo-700 font-medium'>
            <Settings className='w-3.5 h-3.5 inline mr-1 -mt-0.5' /> 
            Hierarchy is dynamically generated based on user profiles' reporting lines (Mocked for this visualization).
        </div>
    </Panel>
);


// --- Main Component ---

export default function App() {
  const [departments, setDepartments] = useState(MOCK_DEPARTMENTS);
  const [roles, setRoles] = useState(MOCK_ROLES);
  const [hierarchy] = useState(MOCK_HIERARCHY); // Hierarchy is read-only for this component

  const { notify, message, visible, type } = useNotification();
  
  // Font imports are handled via CDN links in the HTML wrapper.
  const pageStyle = {
    fontFamily: '"Roboto", sans-serif', // Default body text
  };
  
  const notificationColor = useMemo(() => {
    if (type === 'success') return 'bg-green-600';
    if (type === 'danger') return 'bg-red-600';
    return 'bg-teal-600'; // Info is teal
  }, [type]);

  return (
    <div className="p-4 sm:p-8 bg-gray-100 min-h-screen" style={pageStyle}>
      
      {/* HEADER */}
      <div className="mb-8 border-b border-gray-200 pb-5">
        {/* Using Poppins for the main heading */}
        <h1 className="text-3xl font-bold text-gray-800 flex items-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
          <Users className="w-8 h-8 mr-3 text-teal-600" />
          HRMS Portal: Organizational Management
        </h1>
        {/* Increased subtext size to text-sm */}
        <p className="text-sm text-gray-500 mt-2">
          Centralized configuration panel for enterprise organizational data, including departments, roles, compensation, and hierarchy visualization.
        </p>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        
        {/* Left Column (Departments & Hierarchy View) */}
        <div className="lg:col-span-1 space-y-6">
          <DepartmentManagement 
            departments={departments}
            setDepartments={setDepartments}
            notify={notify}
          />
          <HierarchyView 
            hierarchy={hierarchy} 
          />
        </div>

        {/* Right Column (Role Definitions) */}
        <div className="lg:col-span-2 space-y-6">
          <RoleManagement 
            roles={roles}
            departments={departments}
            setRoles={setRoles}
            notify={notify}
          />
        </div>
      </div>

      {/* Custom Notification Box (Toast) */}
      <div 
        className={`fixed bottom-6 right-6 p-4 rounded-xl shadow-2xl text-sm font-medium transition-all duration-300 transform 
                    ${visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'} ${notificationColor} text-white`}
      >
        <div className='flex items-center space-x-2'>
            <Check className='w-4 h-4' />
            <span>{message}</span>
        </div>
      </div>
    </div>
  );
}